import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import SlugResolver from './pages/catalog/SlugResolver';
import CartPage from './pages/CartPage';
import GetQuotePage from './pages/GetQuotePage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ContactPage from './pages/ContactPage';
import ServicesPage from './pages/ServicesPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import AboutPage from './pages/AboutPage';
import NotFoundPage from './pages/NotFoundPage';
import AdminPage from './pages/AdminPage';
import BusinessRegisterPage from './pages/BusinessRegisterPage';
import NewBusinessRegisterPage from './pages/NewBusinessRegisterPage';
import { GooglePhonePage, ForgotPasswordPage } from './pages/AuthPages';
import { TermsPage, PrivacyPage, ShippingPage } from './pages/LegalPages';

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin' && user.role !== 'staff') return <Navigate to="/" replace />;
  return children;
};

const WithLayout = ({ children }) => (
  <>
    <Navbar />
    <main className="main-content">{children}</main>
    <Footer />
  </>
);

// ── Legacy redirect: /products/:seg1 → /:seg1  (category pages)
const LegacyProductsRedirect1 = () => {
  const { seg1 } = useParams();
  return <Navigate to={`/${seg1}`} replace />;
};

// ── Legacy redirect: /products/:seg1/:seg2 → /category/:seg1/:seg2
const LegacyProductsRedirect2 = () => {
  const { seg1, seg2 } = useParams();
  return <Navigate to={`/category/${seg1}/${seg2}`} replace />;
};

// ── Legacy redirect: /products/:seg1/:seg2/:seg3 → /category/:seg1/:seg2/:seg3
const LegacyProductsRedirect3 = () => {
  const { seg1, seg2, seg3 } = useParams();
  return <Navigate to={`/category/${seg1}/${seg2}/${seg3}`} replace />;
};

const AppRoutes = () => (
  <Routes>
    {/* ── Admin ── */}
    <Route path="/admin/*" element={
      <AdminRoute><AdminPage /></AdminRoute>
    } />

    {/* ── Auth & Business ── */}
    <Route path="/business/register" element={
      <ProtectedRoute><BusinessRegisterPage /></ProtectedRoute>
    } />
    <Route path="/business/new-register" element={
      <WithLayout><NewBusinessRegisterPage /></WithLayout>
    } />
    <Route path="/google-phone" element={
      <ProtectedRoute><GooglePhonePage /></ProtectedRoute>
    } />

    {/* ── Home ── */}
    <Route path="/" element={<WithLayout><HomePage /></WithLayout>} />

    {/* ── Fixed public routes — must be before slug routes ── */}
    <Route path="/products"        element={<WithLayout><ProductsPage /></WithLayout>} />
    <Route path="/cart"            element={<WithLayout><CartPage /></WithLayout>} />
    <Route path="/get-quote"       element={<WithLayout><GetQuotePage /></WithLayout>} />
    <Route path="/checkout"        element={<WithLayout><CheckoutPage /></WithLayout>} />
    <Route path="/login"           element={<WithLayout><LoginPage /></WithLayout>} />
    <Route path="/forgot-password" element={<WithLayout><ForgotPasswordPage /></WithLayout>} />
    <Route path="/register"        element={<WithLayout><RegisterPage /></WithLayout>} />
    <Route path="/services"        element={<WithLayout><ServicesPage /></WithLayout>} />
    <Route path="/services/:slug"  element={<WithLayout><ServiceDetailPage /></WithLayout>} />
    <Route path="/about"           element={<WithLayout><AboutPage /></WithLayout>} />
    <Route path="/contact"         element={<WithLayout><ContactPage /></WithLayout>} />
    <Route path="/terms"                  element={<WithLayout><TermsPage /></WithLayout>} />
    <Route path="/terms-and-conditions"   element={<WithLayout><TermsPage /></WithLayout>} />
    <Route path="/privacy"                element={<WithLayout><PrivacyPage /></WithLayout>} />
    <Route path="/privacy-policy"         element={<WithLayout><PrivacyPage /></WithLayout>} />
    <Route path="/shipping"               element={<WithLayout><ShippingPage /></WithLayout>} />
    <Route path="/shipping-policy"        element={<WithLayout><ShippingPage /></WithLayout>} />
    <Route path="/dashboard/*" element={
      <ProtectedRoute>
        <WithLayout><DashboardPage /></WithLayout>
      </ProtectedRoute>
    } />

    {/* ── NEW catalog routes ── */}
    {/* domain/:categorySlug  (category page at root level) */}
    <Route path="/:categorySlug" element={<WithLayout><SlugResolver /></WithLayout>} />

    {/* domain/category/:cat/:sub/:product  (product detail with subcategory) */}
    <Route path="/category/:seg1/:seg2/:seg3" element={<WithLayout><ProductDetailPage /></WithLayout>} />

    {/* domain/category/:cat/:subOrProduct  (subcategory or product detail) */}
    <Route path="/category/:seg1/:seg2" element={<WithLayout><SlugResolver /></WithLayout>} />

    {/* domain/category/:cat  (fallback — redirects to /:cat) */}
    <Route path="/category/:seg1" element={<WithLayout><SlugResolver /></WithLayout>} />

    {/* ── Legacy /products/:seg* redirects (preserve old links / SEO) ── */}
    <Route path="/products/:seg1/:seg2/:seg3" element={<LegacyProductsRedirect3 />} />
    <Route path="/products/:seg1/:seg2"       element={<LegacyProductsRedirect2 />} />
    <Route path="/products/:seg1"             element={<LegacyProductsRedirect1 />} />

    {/* ── 404 ── */}
    <Route path="*" element={<WithLayout><NotFoundPage /></WithLayout>} />
  </Routes>
);

const App = () => (
  <HelmetProvider>
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <ScrollToTop />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                borderRadius: '10px',
                fontFamily: 'var(--font-body)',
                fontSize: '0.9rem',
              },
            }}
          />
          <AppRoutes />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  </HelmetProvider>
);

export default App;